/**
 * Push-to-Talk Hook
 *
 * Manages push-to-talk keyboard event handling and state.
 * Monitors the configured key binding and triggers voice input
 * when the key is pressed and held.
 */

"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useCallback, useRef } from "react";
import { isPushToTalkEnabled, getPushToTalkKeybind, matchesKeybind, type KeyBinding } from "@/lib/pushToTalkStorage";
type UsePushToTalkOptions = {
  onPushStart?: () => void;
  onPushEnd?: () => void;
  disabled?: boolean;
};
type UsePushToTalkResult = {
  isPushing: boolean;
  isEnabled: boolean;
  keybind: KeyBinding;
};
export function usePushToTalk(t0) {
  const $ = _c(26);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const {
    onPushStart,
    onPushEnd,
    disabled: t2
  } = options;
  const disabled = t2 === undefined ? false : t2;
  const [isPushing, setIsPushing] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      key: " "
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [keybind, setKeybind] = useState(t3);
  const pushingRef = useRef(false);
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setIsEnabled(isPushToTalkEnabled());
      setKeybind(getPushToTalkKeybind());
    };
    t5 = [];
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const handleStorageChange = () => {
        setIsEnabled(isPushToTalkEnabled());
        setKeybind(getPushToTalkKeybind());
      };
      window.addEventListener("storage", handleStorageChange);
      return () => window.removeEventListener("storage", handleStorageChange);
    };
    t7 = [];
    $[5] = t6;
    $[6] = t7;
  } else {
    t6 = $[5];
    t7 = $[6];
  }
  useEffect(t6, t7);
  let t8;
  if ($[7] !== disabled || $[8] !== isEnabled || $[9] !== keybind || $[10] !== onPushStart) {
    t8 = event => {
      if (disabled || !isEnabled || pushingRef.current) {
        return;
      }
      const target = event.target as HTMLElement;
      const isInInput = target.tagName === "INPUT" || target.tagName === "SELECT" || target.isContentEditable;
      const isTextarea = target.tagName === "TEXTAREA";
      if (isInInput && !isTextarea) {
        return;
      }
      if (matchesKeybind(event, keybind)) {
        event.preventDefault();
        pushingRef.current = true;
        setIsPushing(true);
        onPushStart?.();
      }
    };
    $[7] = disabled;
    $[8] = isEnabled;
    $[9] = keybind;
    $[10] = onPushStart;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const handleKeyDown = t8;
  let t9;
  if ($[12] !== keybind || $[13] !== onPushEnd) {
    t9 = event_0 => {
      if (!pushingRef.current) {
        return;
      }
      if (matchesKeybind(event_0, keybind)) {
        event_0.preventDefault();
        pushingRef.current = false;
        setIsPushing(false);
        onPushEnd?.();
      }
    };
    $[12] = keybind;
    $[13] = onPushEnd;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const handleKeyUp = t9;
  let t10;
  let t11;
  if ($[15] !== disabled || $[16] !== handleKeyDown || $[17] !== handleKeyUp || $[18] !== isEnabled || $[19] !== onPushEnd) {
    t10 = () => {
      if (!isEnabled || disabled) {
        if (pushingRef.current) {
          pushingRef.current = false;
          setIsPushing(false);
          onPushEnd?.();
        }
        return;
      }
      window.addEventListener("keydown", handleKeyDown);
      window.addEventListener("keyup", handleKeyUp);
      return () => {
        window.removeEventListener("keydown", handleKeyDown);
        window.removeEventListener("keyup", handleKeyUp);
        if (pushingRef.current) {
          pushingRef.current = false;
          setIsPushing(false);
          onPushEnd?.();
        }
      };
    };
    t11 = [isEnabled, disabled, handleKeyDown, handleKeyUp, onPushEnd];
    $[15] = disabled;
    $[16] = handleKeyDown;
    $[17] = handleKeyUp;
    $[18] = isEnabled;
    $[19] = onPushEnd;
    $[20] = t10;
    $[21] = t11;
  } else {
    t10 = $[20];
    t11 = $[21];
  }
  useEffect(t10, t11);
  let t12;
  if ($[22] !== isEnabled || $[23] !== isPushing || $[24] !== keybind) {
    t12 = {
      isPushing,
      isEnabled,
      keybind
    };
    $[22] = isEnabled;
    $[23] = isPushing;
    $[24] = keybind;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  return t12;
}